/**
 * Aba Auto-close em leitura (PR2).
 *
 * Renderiza fonte REAL (R15):
 *   - `autoCloseable` (bool) → estado habilitado/desabilitado
 *   - `autoCloseableOutcomes` (string[]) → resultados permitidos (chips)
 *   - `outcomeReasonCatalog` (Record<outcome, razões[]> | null) → catálogo de
 *     razões por resultado, agrupado.
 *
 * Outcomes canônicos (ADR-054) ganham rótulo PT via `autoCloseOutcomeLabel`
 * (id cru fica no `title` para diagnóstico; desconhecido degrada para o id).
 * Razões são valores de config do tenant — exibidas como estão (mono).
 * Estatísticas de uso do protótipo (KPIs 30d) não têm fonte → omitidas.
 */

import { autoCloseOutcomeLabel } from '../playbook-labels';

import type { PlaybookDto } from '../types';

export function TabAutoClose({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const { autoCloseable, autoCloseableOutcomes, outcomeReasonCatalog } = playbook;
  const catalogEntries = outcomeReasonCatalog !== null ? Object.entries(outcomeReasonCatalog) : [];

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Auto-close">
      <h2 className="pb-panel__title">Auto-close</h2>

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Fechamento automático pela IA</span>
        <div className="pb-inline-value">
          <span className={'pb-badge ' + (autoCloseable ? 'pb-badge--on' : 'pb-badge--off')}>
            {autoCloseable ? 'Habilitado' : 'Desabilitado'}
          </span>
          <span className="pb-read-value pb-read-value--muted">
            {autoCloseable
              ? 'A IA pode encerrar conversas deste playbook quando o resultado é permitido.'
              : 'A IA não encerra conversas deste playbook automaticamente.'}
          </span>
        </div>
      </div>

      {autoCloseable && (
        <>
          <div className="pb-panel__divider" />
          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Resultados permitidos para auto-close</span>
            {autoCloseableOutcomes.length > 0 ? (
              <div className="pb-chip-row">
                {autoCloseableOutcomes.map((outcome) => (
                  <span key={outcome} className="pb-chip" title={outcome}>
                    {autoCloseOutcomeLabel(outcome)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="pb-read-value pb-read-value--muted">Nenhum resultado permitido</p>
            )}
          </div>
        </>
      )}

      <div className="pb-panel__divider" />

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Catálogo de razões por resultado</span>
        {catalogEntries.length > 0 ? (
          <div className="pb-catalog">
            {catalogEntries.map(([outcome, reasons]) => (
              <div key={outcome} className="pb-catalog__group">
                <span className="pb-catalog__outcome" title={outcome}>
                  {autoCloseOutcomeLabel(outcome)}
                </span>
                <div className="pb-chip-row">
                  {reasons.map((reason) => (
                    <span key={reason} className="pb-chip pb-chip--mono">
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="pb-read-value pb-read-value--muted">Nenhum catálogo de razões definido</p>
        )}
      </div>
    </div>
  );
}
